import styled from 'styled-components';
import React, { useState } from 'react';

import {
  DropDownItem,
  DropDownList,
  DropDownSearch,
  useDropDownLogic,
  DropDownContainer
} from 'components/DropDown';
import { SearchIcon } from 'components/Icons';
import { useDebounce } from 'hooks/useDebounce';

const SearchIconContainer = styled.span`
  ${({ theme }) => `
  right: 0px;
  padding: 16px 12px;
  position: absolute;

  svg {
    height: 20px;
    width: 20px;
    path {
      fill: ${theme.colors.greyRegular};
    }
  }
  `}
`;

const Loading = styled.div`
  padding: 18px;
`;

const wrapAround = (value, delta, min, max) =>
  delta >= 0
    ? (value + delta - min) % (max + min)
    : (value + delta + max * -delta - min) % (max + min);

export const Search = ({
  placeholder = 'Search',
  onChange,
  onItemSelect,
  initialResults = []
}) => {
  const [itemIdx, setItemIdx] = useState(-1);
  const [results, setResults] = useState(initialResults);
  const [isLoading, setIsLoading] = useState(false);

  const {
    dropDownRef,
    opened,
    setOpened,
    searchTerm,
    setSearchTerm
  } = useDropDownLogic({});

  const decoratedItemSelect = (...args) => {
    onItemSelect(...args);
    setOpened(false);
  };

  const onKeyDown = (e, searchTerm) => {
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      const resultAmount = results.length;

      e.preventDefault();
      const newIndex = wrapAround(
        itemIdx,
        e.key === 'ArrowUp' ? -1 : 1,
        0,
        resultAmount
      );
      setItemIdx(newIndex);
    }

    if (e.key === 'Enter') {
      e.preventDefault();
      if (results[itemIdx] !== undefined) {
        decoratedItemSelect(searchTerm, results[itemIdx]);
      }
    }
  };

  const debouncedSearch = useDebounce(async e => {
    setIsLoading(true);

    const myResults = await onChange(e.target.value, results);

    setResults(myResults);
    setOpened(true);
    setIsLoading(false);
  }, 1000);

  return (
    <DropDownContainer ref={dropDownRef} opened={opened}>
      <DropDownSearch
        onFocus={() => setOpened(true)}
        onChange={async e => {
          setSearchTerm(e.target.value);
          debouncedSearch(e);
        }}
        onKeyDown={e => onKeyDown(e, searchTerm)}
        placeholder={placeholder}
      />
      <SearchIconContainer>
        <SearchIcon />
      </SearchIconContainer>
      {isLoading && (
        <DropDownList opened={opened}>
          <Loading>Loading....</Loading>
        </DropDownList>
      )}
      {results.length > 0 && opened && !isLoading && (
        <DropDownList opened={opened}>
          {results.length > 0 &&
            results.map((item, index) => (
              <DropDownItem
                onClick={() => decoratedItemSelect(searchTerm, item)}
                name="search-item"
                value={item.title}
                onMouseEnter={() => {
                  setItemIdx(index);
                }}
                selected={itemIdx === index}
                key={index}
              >
                {item.title}
              </DropDownItem>
            ))}
        </DropDownList>
      )}
    </DropDownContainer>
  );
};
